// Backoffice staff LINE link + "Login with LINE" — pure helpers.
//
// Spec: docs/specs/2026-10-03-bo-staff-line-login-design.md
//   §2 API contract + error table, §5 UI, §6 edge cases.
//
// WHY THIS FILE EXISTS. The three screens that touch LINE (the login page, the
// Edit Profile card and the OAuth callback page) all have to answer the same
// four questions: which query params came back from LINE, which flow we are in,
// which i18n key a failure maps to, and whether this profile is linked. This
// repo's jest cannot mount a .vue (see memory reference-bo-fn-jest-infra), so
// if those answers lived inside the components they would be untestable. They
// live here instead: no Vue, no axios, no store, and the one browser API we
// need (sessionStorage) is injected.
//
// SECURITY NOTE. `code` and `state` pass THROUGH this module and must never be
// logged, stored in localStorage, or put in a URL we build. The BE owns state
// generation and verification (spec §1 "Security invariants"); nothing here may
// decide that a callback is trustworthy.

// LINE's brand green. Used by the login button and the connect button; the spec
// names the literal (#06C755) in §5, so it is exported rather than retyped.
export const LINE_BRAND_COLOR = "#06C755";

export const LINE_PURPOSE_LOGIN = "login";
export const LINE_PURPOSE_LINK = "link";

// sessionStorage key the login page / profile card write before handing the
// browser to LINE, and the callback page reads to decide which endpoint to
// call. sessionStorage (not localStorage) on purpose: it dies with the tab, so
// an abandoned flow cannot steer a callback opened days later.
export const LINE_PURPOSE_STORAGE_KEY = "line_purpose";

// The error codes the API answers with (spec §2 error table + the 429 added by
// bo-api #785). Stored bare; the i18n keys are these prefixed with
// "errors.auth.".
export const LINE_ERROR_CODES = [
    "line_disabled",
    "line_state_invalid",
    "line_code_invalid",
    "line_verify_failed",
    "line_not_linked",
    "line_already_linked",
    "line_already_linked_self",
    // bo-api #785: the per-IP cap on authorize-url + callback
    // (boline:state:ip:{ip}, 20 per 10 minutes — spec §1) now answers 429
    // instead of letting the request through.
    "line_too_many"
];

// HTTP status → error code, for the case where the response body is missing or
// unparseable (network error, proxy 502 with an HTML body). Taken straight from
// the spec §2 table; 400 maps to line_state_invalid because that is the only
// 400 the FE can provoke without the user retyping something — we always send
// the `code` LINE handed us.
const STATUS_TO_CODE = {
    400: "line_state_invalid",
    403: "line_not_linked",
    409: "line_already_linked",
    429: "line_too_many",
    502: "line_verify_failed",
    503: "line_disabled"
};

const str = value => (typeof value === "string" ? value.trim() : "");

// An i18n key is a dotted token with no whitespace. This is what lets the same
// code read BOTH shapes of the error body safely (see lineErrorKeyFromError):
// a developer detail like "sql: no rows in result set" or "invalid_grant" is
// not keyish and is never handed to $t(), where vue-i18n would print it raw.
//
// The namespace is pinned to `errors.` / `auth.` deliberately. Accepting any
// dotted token would let a detail string such as "redis.nil" through and put
// "redis.nil" on screen as if it were a translation.
const isI18nKeyish = value => {
    const s = str(value);
    if (!s || /\s/.test(s)) return false;
    if (!/^[A-Za-z0-9_.]+$/.test(s)) return false;
    return s.indexOf("errors.") === 0 || s.indexOf("auth.") === 0;
};

// keyFromCode resolves ONE candidate string to an i18n key, or "" if it is not
// one. Returning "" rather than a guess is what makes the precedence in
// lineErrorKeyFromError work: an unusable candidate falls through to the next.
const keyFromCode = code => {
    const raw = str(code);
    if (!raw) return "";

    const bare = raw.replace(/^errors\.auth\./, "");
    if (LINE_ERROR_CODES.indexOf(bare) !== -1) return "errors.auth." + bare;

    // status 401 reuses the password path's blocked / unverified wording
    // verbatim (spec §6 ข้อ 2) — the LINE door must not enumerate accounts any
    // better than the password door does.
    const asAuth = raw.replace(/^auth\./, "");
    if (asAuth === "block" || asAuth === "unverify") return "auth." + asAuth;

    // Any other key the API owns is passed through untouched: inventing a
    // FE-side message for a BE error the FE has never heard of is how a real
    // cause gets buried under "เข้าสู่ระบบผิดพลาด".
    return isI18nKeyish(raw) ? raw : "";
};

const keyFromStatus = status => {
    const code = STATUS_TO_CODE[Number(status)];
    return code ? "errors.auth." + code : "";
};

// parseSearch turns "?code=a&state=b" into a plain object. Written out rather
// than using URLSearchParams so the helper also runs under jest's jsdom-free
// paths and under Node 16 without a polyfill discussion.
const parseSearch = search => {
    const out = {};
    const body = String(search).replace(/^[?#]/, "");
    if (!body) return out;
    body.split("&").forEach(pair => {
        if (!pair) return;
        const eq = pair.indexOf("=");
        const rawKey = eq === -1 ? pair : pair.slice(0, eq);
        const rawVal = eq === -1 ? "" : pair.slice(eq + 1);
        let key;
        let val;
        try {
            key = decodeURIComponent(rawKey.replace(/\+/g, " "));
            val = decodeURIComponent(rawVal.replace(/\+/g, " "));
        } catch (e) {
            // A malformed %-escape must not throw on the callback page — a
            // thrown parse error there is a blank white screen with no way out.
            key = rawKey;
            val = rawVal;
        }
        if (key && out[key] === undefined) out[key] = val;
    });
    return out;
};

// lineCallbackParams reads the four things LINE (or our BE's echo) can put on
// /auth/line/callback. Accepts either a Vue `$route.query` object or a raw
// "?a=b" string so the callback page works whether it is reached through the
// router or on a cold load.
//
// Repeated params (?code=x&code=y) arrive from vue-router as an array; only the
// FIRST is used. Taking the last would let an attacker append a param to a
// legitimate link and have us submit theirs.
export function lineCallbackParams(query) {
    let q = query;
    if (typeof query === "string") q = parseSearch(query);
    if (!q || typeof q !== "object") q = {};

    const pick = name => {
        const v = q[name];
        if (Array.isArray(v)) return str(v[0]);
        return str(v);
    };

    return {
        code: pick("code"),
        state: pick("state"),
        error: pick("error"),
        purpose: pick("purpose")
    };
}

// lineResolvePurpose decides which flow the callback belongs to.
//
// The value WE wrote to sessionStorage wins over the one in the URL: the query
// string is attacker-supplied, and while sending a link-state to the login
// endpoint only earns a 400 from the BE (it checks purpose against the state it
// minted), preferring our own copy keeps the error the user sees honest.
//
// Defaults to "login" when neither is present (back button into a dead tab):
// that path ends on /login with a readable message, whereas defaulting to
// "link" would bounce a logged-out visitor through a 401 and lose the reason.
export function lineResolvePurpose(storedPurpose, queryPurpose) {
    const valid = v => (v === LINE_PURPOSE_LOGIN || v === LINE_PURPOSE_LINK ? v : "");
    return valid(str(storedPurpose)) || valid(str(queryPurpose)) || LINE_PURPOSE_LOGIN;
}

// lineErrorKey maps (HTTP status, one API code) to an i18n key for the swal
// title. The API owns the message, so a key it sends wins; the status map is
// only the fallback for a missing or unparseable body.
export function lineErrorKey(status, code) {
    return keyFromCode(code) || keyFromStatus(status) || "errors.auth.login_fail";
}

// lineErrorKeyFromError is lineErrorKey applied to an axios rejection. Kept
// beside it so the three screens cannot each invent their own way of digging
// the key out of the response.
//
// WHY IT READS BOTH FIELDS, IN THIS ORDER. The design doc (§2) says the body is
// `{"msg": "<i18n key>", "error": "<dev detail>"}`, but bo-api #785 landed with
// those two swapped — `error` carries the i18n key. Rather than pick a side and
// be wrong half the time, both are tried and the first one that actually LOOKS
// like an i18n key wins (isI18nKeyish), with `error` first as #785 specifies.
// A developer detail in either slot ("sql: no rows…", "invalid_grant") is not
// keyish, so it is skipped instead of being printed at the user.
//
// This means the screens keep showing the right sentence whichever way the BE
// settles the question, and nobody has to coordinate a simultaneous deploy.
export function lineErrorKeyFromError(err) {
    const res = (err && err.response) || {};
    const data = res.data && typeof res.data === "object" ? res.data : {};
    return (
        keyFromCode(data.error) ||
        keyFromCode(data.msg) ||
        keyFromStatus(res.status) ||
        "errors.auth.login_fail"
    );
}

// lineIsDisabled — "did the API say the feature is off?" (spec §5: a 503 hides
// the card instead of leaving a button that always fails, and §6 ข้อ 8: the SQL
// may not be applied yet). Checked on both the status and the code because a
// proxy can turn the body into HTML while keeping the 503.
export function lineIsDisabled(err) {
    const res = (err && err.response) || {};
    if (Number(res.status) === 503) return true;
    // Both slots, for the same reason lineErrorKeyFromError reads both: the
    // design doc and bo-api #785 disagree about which of `msg` / `error`
    // carries the i18n key.
    return lineErrorKeyFromError(err) === "errors.auth.line_disabled";
}

// lineLinkFields normalises the four LINE columns out of GET /api/v1/profile's
// `Datum` (spec §2 #6) or out of POST .../line/link's body (#4). An API that
// does not send them at all (older build, or the HasColumn guard tripped) must
// read as "not linked" — never as "linked to an empty account".
export function lineLinkFields(source) {
    const s = source && typeof source === "object" ? source : {};
    const take = (snake, pascal) => {
        const v = s[snake] !== undefined && s[snake] !== null ? s[snake] : s[pascal];
        if (v === null || v === undefined || v === false) return "";
        return typeof v === "string" ? v.trim() : String(v);
    };
    return {
        line_user_id: take("line_user_id", "LineUserID"),
        line_display_name: take("line_display_name", "LineDisplayName"),
        line_picture_url: take("line_picture_url", "LinePictureURL"),
        line_linked_at: take("line_linked_at", "LineLinkedAt")
    };
}

// isLineLinked — the single rule the card's two states hang off. Only the id
// counts: LINE lets a user have no display name and no picture, so keying off
// the name would show "ยังไม่ได้เชื่อมต่อ" to someone who is linked and then
// 409 them when they press connect again.
export function isLineLinked(profile) {
    return lineLinkFields(profile).line_user_id !== "";
}

// staffSessionUser builds the object handed to Auth/setUser from a login
// response body.
//
// can_view_cost / can_create_coupon_event ride BESIDE User and are deliberately
// not JWT claims (bo-api #594 §4.5, coupon-event spec §2), which means whoever
// reads a login response has to lift them by hand. The password path does that
// inline in Login.vue; the LINE path calls this so the two cannot drift —
// spec §1 "logic ซ้ำกันห้ามแตกต่าง". Missing must read as false: an older API
// that omits a flag means "no", never "everyone".
export function staffSessionUser(data) {
    const body = data && typeof data === "object" ? data : {};
    const user = body.User && typeof body.User === "object" ? body.User : {};
    return Object.assign({}, user, {
        CanViewCost: body.can_view_cost === true,
        CanCreateCouponEvent: body.can_create_coupon_event === true,
        // spec 2026-10-16 §6: Seller Center permissions (undefined on an older API)
        SellerAccess: body.seller_access
    });
}

// --- sessionStorage wrappers -------------------------------------------------
// Injectable storage so specs do not need jsdom, and try/catch because Safari
// in private mode throws on setItem. A failed write must not stop the OAuth
// hand-off: the callback page falls back to the `purpose` query in that case.

const defaultStorage = () => {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : null;
    } catch (e) {
        return null;
    }
};

export function rememberLinePurpose(purpose, storage) {
    const s = storage === undefined ? defaultStorage() : storage;
    const value = purpose === LINE_PURPOSE_LINK ? LINE_PURPOSE_LINK : LINE_PURPOSE_LOGIN;
    try {
        if (s) s.setItem(LINE_PURPOSE_STORAGE_KEY, value);
    } catch (e) {
        /* private mode — the ?purpose echo is the fallback */
    }
    return value;
}

export function readLinePurpose(storage) {
    const s = storage === undefined ? defaultStorage() : storage;
    try {
        return s ? str(s.getItem(LINE_PURPOSE_STORAGE_KEY)) : "";
    } catch (e) {
        return "";
    }
}

export function forgetLinePurpose(storage) {
    const s = storage === undefined ? defaultStorage() : storage;
    try {
        if (s) s.removeItem(LINE_PURPOSE_STORAGE_KEY);
    } catch (e) {
        /* nothing to do — the key dies with the tab anyway */
    }
}

export default {
    LINE_BRAND_COLOR,
    LINE_PURPOSE_LOGIN,
    LINE_PURPOSE_LINK,
    LINE_PURPOSE_STORAGE_KEY,
    LINE_ERROR_CODES,
    lineCallbackParams,
    lineResolvePurpose,
    lineErrorKey,
    lineErrorKeyFromError,
    lineIsDisabled,
    lineLinkFields,
    isLineLinked,
    staffSessionUser,
    rememberLinePurpose,
    readLinePurpose,
    forgetLinePurpose
};
